import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  Fingerprint, 
  Camera, 
  Upload, 
  ShieldCheck, 
  Clock,
  AlertCircle,
  CheckCircle2,
  FileText,
  IdCard,
  Car,
  XCircle,
  RefreshCw,
  Eye,
  Download,
  Calendar,
  TrendingUp,
  Award,
  Clock4,
  X,
  FileBarChart
} from "lucide-react";
import axiosInstance from "../../../../services/server/axios";
import { message } from "antd";

const IdentityVerification = () => {
  const { t } = useTranslation();
  const [selectedDocument, setSelectedDocument] = useState("");
  const [verificationStep, setVerificationStep] = useState("select");
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [frontImageFile, setFrontImageFile] = useState(null);
  const [backImageFile, setBackImageFile] = useState(null);
  const [selfieImageFile, setSelfieImageFile] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState("pending");
  const [currentVerificationId, setCurrentVerificationId] = useState(null);
  const [verificationHistory, setVerificationHistory] = useState([]);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const documentTypes = [
    {
      id: "national_id",
      label: t("national_id"),
      description: t("national_id_desc"),
      icon: IdCard,
      requiresBack: true
    },
    {
      id: "passport",
      label: t("passport"),
      description: t("passport_desc"),
      icon: FileText,
      requiresBack: false
    },
    {
      id: "driver_license",
      label: t("driver_license"),
      description: t("driver_license_desc"),
      icon: Car,
      requiresBack: true
    }
  ];

  // Charger l'historique des vérifications
  useEffect(() => {
    fetchVerificationHistory();
    checkCurrentStatus();
  }, []);

  const fetchVerificationHistory = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.get("/identity-verification", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setVerificationHistory(response.data.verifications);
      }
    } catch (error) {
      console.error("Error fetching verification history:", error);
    }
  };

  const checkCurrentStatus = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.get("/identity-verification/status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        const { status, latest_verification } = response.data;
        
        if (latest_verification && latest_verification.status === "processing") {
          setCurrentVerificationId(latest_verification.id);
          setVerificationStatus("processing");
        } else if (latest_verification && latest_verification.status === "verified") {
          setVerificationStatus("verified");
        }
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
    }
  };

  const handleDocumentSelect = (documentId) => {
    setSelectedDocument(documentId);
    setVerificationStep("upload");
  };

  const handleImageUpload = (type, file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === "front") {
        setFrontImage(e.target.result);
        setFrontImageFile(file);
      }
      if (type === "back") {
        setBackImage(e.target.result);
        setBackImageFile(file);
      }
      if (type === "selfie") {
        setSelfieImage(e.target.result);
        setSelfieImageFile(file);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleVerificationSubmit = async () => {
    if (!frontImageFile || !selfieImageFile) {
      message.error(t("please_upload_all_required_images"));
      return;
    }

    setIsVerifying(true);

    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      
      formData.append("document_type", selectedDocument);
      formData.append("front_image", frontImageFile);
      formData.append("selfie_image", selfieImageFile);
      
      if (documentTypes.find(d => d.id === selectedDocument)?.requiresBack && backImageFile) {
        formData.append("back_image", backImageFile);
      }

      const response = await axiosInstance.post("/identity-verification", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        setCurrentVerificationId(response.data.verification_id);
        setVerificationStatus("processing");
        setVerificationStep("success");
        message.success(t("verification_submitted_successfully"));
        
        // Rafraîchir l'historique
        fetchVerificationHistory();
      } else {
        message.error(response.data.message || t("verification_submission_failed"));
      }
    } catch (error) {
      console.error("Verification submission error:", error);
      
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0]?.[0];
        message.error(firstError || t("validation_error"));
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error(t("verification_submission_failed"));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancelVerification = async (verificationId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.delete(`/identity-verification/${verificationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        message.success(t("verification_cancelled_successfully"));
        setVerificationHistory(prev => 
          prev.filter(v => v.id !== verificationId)
        );
        setCurrentVerificationId(null);
        setVerificationStatus("pending");
      }
    } catch (error) {
      console.error("Error cancelling verification:", error);
      message.error(t("cancellation_failed"));
    }
  };

  const showVerificationDetails = async (verification) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.get(`/identity-verification/${verification.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setSelectedVerification(response.data.verification);
        setDetailModalVisible(true);
      }
    } catch (error) {
      console.error("Error fetching verification details:", error);
      message.error(t("fetch_details_error"));
    }
  };

  const getDocumentRequirements = () => {
    const doc = documentTypes.find(d => d.id === selectedDocument);
    if (!doc) return [];
    
    const requirements = [
      t("document_requirement_clear"),
      t("document_requirement_full"),
      t("document_requirement_valid")
    ];
    
    if (doc.id === "national_id") {
      requirements.push(t("national_id_both_sides"));
    } else if (doc.id === "driver_license") {
      requirements.push(t("license_both_sides"));
    }
    
    return requirements;
  };

  const getStatusConfig = (status) => {
    const config = {
      pending: { 
        color: "var(--primary-color)",
        icon: <Clock4 size={16} className="text-amber-600" />,
        text: t("pending")
      },
      processing: { 
        color: "var(--primary-color)",
        icon: <RefreshCw size={16} className="text-blue-600" />,
        text: t("processing")
      },
      verified: { 
        color: "var(--primary-color8)",
        icon: <CheckCircle2 size={16} className="text-emerald-600" />,
        text: t("verified")
      },
      rejected: { 
        color: "var(--primary-color5)",
        icon: <XCircle size={16} className="text-red-600" />,
        text: t("rejected")
      }
    };
    return config[status] || config.pending;
  };

  const getDocumentIcon = (documentType) => {
    const icons = {
      national_id: <IdCard size={20} className="text-blue-600" />,
      passport: <FileText size={20} className="text-purple-600" />,
      driver_license: <Car size={20} className="text-green-600" />
    };
    return icons[documentType] || <FileText size={20} />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (!score) return "text-gray-500";
    if (score >= 0.8) return "text-emerald-600";
    if (score >= 0.6) return "text-amber-600";
    return "text-red-600";
  };

  const renderStepIndicator = () => {
    const steps = [
      { key: "select", label: t("select_document") },
      { key: "upload", label: t("upload_documents") },
      { key: "selfie", label: t("take_selfie") },
      { key: "review", label: t("review_submit") }
    ];
    
    return (
      <div className="d-flex justify-content-between align-items-center mb-8 position-relative">
        <div className="position-absolute top-50 start-0 end-0 translate-middle-y">
          <div 
            className="progress" 
            style={{ 
              height: "3px",
              backgroundColor: "var(--primary-color8)",
            }}
          >
            <div 
              className="progress-bar bg-primary"
              style={{
                width: `${(steps.findIndex(s => s.key === verificationStep) / (steps.length - 1)) * 100}%`,
                transition: "width 0.3s ease"
              }}
            />
          </div>
        </div>
        
        {steps.map((step, index) => {
          const isActive = step.key === verificationStep;
          const isCompleted = steps.findIndex(s => s.key === verificationStep) > index;
          
          return (
            <div key={step.key} className="d-flex flex-column align-items-center position-relative z-1">
              <div 
                className={`rounded-circle d-flex align-items-center justify-content-center ${
                  isActive ? "bg-primary text-white shadow" : 
                  isCompleted ? "bg-success text-white" : "bg-light border"
                }`}
                style={{
                  width: "50px",
                  height: "50px",
                  transition: "all 0.3s ease",
                }}
              >
                {isCompleted ? (
                  <CheckCircle2 size={24} />
                ) : (
                  <span className="fw-bold fs-6">{index + 1}</span>
                )}
              </div>
              <small className="mt-2 fw-medium" style={{ 
    color: isActive ? "var(--primary-color5 )" : "var(--primary-color)!important"
  }}>
                {step.label}
              </small>
            </div>
          );
        })}
      </div>
    );
  };

  const resetForm = () => {
    setSelectedDocument("");
    setVerificationStep("select");
    setFrontImage(null);
    setBackImage(null);
    setSelfieImage(null);
    setFrontImageFile(null);
    setBackImageFile(null);
    setSelfieImageFile(null);
    setVerificationStatus("pending");
    setCurrentVerificationId(null);
  };

  // Calcul des statistiques
  const stats = {
    total: verificationHistory.length,
    verified: verificationHistory.filter(v => v.status === 'verified').length,
    pending: verificationHistory.filter(v => v.status === 'pending').length,
    processing: verificationHistory.filter(v => v.status === 'processing').length
  };

  // Si une vérification est en cours, afficher le statut
  if (verificationStatus === "processing" && currentVerificationId) {
    return (
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card border-0 shadow-lg text-center">
              <div className="card-body p-5">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4 bg-primary bg-opacity-10"
                  style={{
                    width: "100px",
                    height: "100px",
                    color: "var(--primary-color3)"
                  }}
                >
                  <RefreshCw size={48} className="spinning" />
                </div>
                <h3 className="card-title mb-3 text-primary">{t("verification_in_progress")}</h3>
                <p className="card-text text-muted mb-4 fs-6">
                  {t("verification_in_progress_desc")}
                </p>
                <div className="d-flex justify-content-center gap-3">
                  <button 
                    className="btn btn-outline-danger btn-lg"
                    onClick={() => handleCancelVerification(currentVerificationId)}
                  >
                    <XCircle size={20} className="me-2" />
                    {t("cancel_verification")}
                  </button>
                  <button 
                    className="btn btn-outline-primary btn-lg"
                    onClick={checkCurrentStatus}
                  >
                    <RefreshCw size={20} className="me-2" />
                    {t("refresh_status")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-xxl-10">
          {/* Header */}
          <div className="text-center mb-5">
            <div className="d-flex justify-content-center align-items-center mb-4">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "var(--primary-color5)",
                  color: "var(--primary-color3)"
                }}
              >
                <Fingerprint size={36} />
              </div>
              <div className="text-start">
                <h1 className="h2 fw-bold mb-2 "style={{ color: "var(--primary-color) !important"  }}>{t("identity_verification")}</h1>
                <p className=" mb-0 fs-6"style={{ color: "var(--primary-color) !important"  }}>
                  {t("identity_verification_desc")}
                </p>
              </div>
            </div>
          </div>

          {/* Statistiques rapides - Design amélioré */}
          {verificationHistory.length > 0 && (
            <div className="row mb-5">
              <div className="col-xl-4 col-md-4 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <div className="card-body text-white">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <TrendingUp size={32} />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h4 className="mb-0 fw-bold">{stats.total}</h4>
                        <p className="mb-0 opacity-75">{t("total")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-4 col-md-4 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                  <div className="card-body text-white">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <Award size={32} />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h4 className="mb-0 fw-bold">{stats.verified}</h4>
                        <p className="mb-0 opacity-75">{t("verified")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-4 col-md-4 mb-3">
                <div className="card border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                  <div className="card-body text-white">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <Clock4 size={32} />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h4 className="mb-0 fw-bold">{stats.pending + stats.processing}</h4>
                        <p className="mb-0 opacity-75">{t("pending")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="col-xl-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100 bg-light">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 text-primary">
                        <Calendar size={32} />
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h4 className="mb-0 fw-bold text-dark">
                          {stats.verified > 0 ? Math.round((stats.verified / stats.total) * 100) : 0}%
                        </h4>
                        <p className="mb-0 text-muted">{t("success_rate")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          )}

          {/* Historique des vérifications - Design moderne */}
          {verificationHistory.length > 0 && (
            <div className="card border-0 shadow-lg mb-5">
              <div className="card-header  border-0 py-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0 fw-bold "style={{color:"var(--primary-color) !important"}}>
                    <FileBarChart size={20} className="me-2" />
                    {t("verification_history")}
                  </h5>
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    onClick={fetchVerificationHistory}
                  >
                    <RefreshCw size={16} className="me-1" />
                    {t("refresh")}
                  </button>
                </div>
              </div>
              
              <div className="card-body p-0"style={{ backgroundColor:"var(--primary-color8) !important"}}>
                <div className="table-responsive"style={{ backgroundColor:"var(--primary-color8) !important"}}>
                  <table className=" align-middle mb-0"style={{ backgroundColor:"var(--primary-color8) !important"}}>
                    <thead className=""style={{ backgroundColor:"var(--primary-color8) !important"}}>
                      <tr>
                        <th className="border-0 ps-4 fw-semibold text-muted text-uppercase small" style={{ width: '25%', color: "var(--primary-color) !important" }}>
                          {t("document_type")}
                        </th>
                        <th className="border-0 fw-semibold text-muted text-uppercase small" style={{ width: '25%',  color: "var(--primary-color) !important"  }}>
                          {t("submission_date")}
                        </th>
                        <th className="border-0 fw-semibold text-muted text-uppercase small" style={{ width: '20%',  color: "var(--primary-color) !important"  }}>
                          {t("status")}
                        </th>
                        {/* <th className="border-0 fw-semibold text-muted text-uppercase small" style={{ width: '15%' }}>
                          {t("confidence_score")}
                        </th> */}
                        <th className="border-0 pe-4 fw-semibold text-muted text-uppercase small text-end" style={{ width: '15%', color: "var(--primary-color) !important"  }}>
                          {t("actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {verificationHistory.map((verification) => {
                        const statusConfig = getStatusConfig(verification.status);
                        return (
                          <tr key={verification.id} className="border-bottom">
                            <td className="ps-4 py-3">
                              <div className="d-flex align-items-center gap-3" style={{ color: "var(--primary-color) !important"  }}>
                                {getDocumentIcon(verification.document_type)}
                                <div>
                                  <div className="fw-medium  text-capitalize "style={{ color: "var(--primary-color) !important"  }}>
                                    {verification.document_type?.replace('_', ' ') || 'N/A'}
                                  </div>
                                  {/* <small className="text-muted">
                                    ID: {verification.id}
                                  </small> */}
                                </div>
                              </div>
                            </td>
                            <td className="py-3">
                              <div className=" fw-medium"style={{ color: "var(--primary-color) !important"  }}>
                                {formatDate(verification.created_at)}
                              </div>
                            </td>
                            <td className="py-3">
                              <span className={`badge border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 ${statusConfig.color}`}style={{ color: "var(--primary-color) !important"  }}>
                                {statusConfig.icon}
                                {statusConfig.text}
                              </span>
                            </td>
                            {/* <td className="py-3">
                              <div className={`fw-bold fs-6 ${getScoreColor(verification.face_match_score)}`}>
                                {verification.face_match_score ? 
                                  `${(verification.face_match_score * 100).toFixed(1)}%` : 
                                  <span className="text-muted">-</span>
                                }
                              </div>
                            </td> */}
                            <td className="pe-4 py-3 text-end">
                              <div className="d-flex justify-content-end gap-2">
                                <button 
                                  className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 px-3"
                                  onClick={() => showVerificationDetails(verification)}
                                  title={t("view_details")}
                                >
                                  <Eye size={16} />
                                  <span className="d-none d-md-inline"style={{ color: "var(--primary-color) !important"  }}>{t("view_details")}</span>
                                </button>
                                {(verification.status === 'pending' || verification.status === 'processing') && (
                                  <button 
                                    className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2 px-3"
                                    onClick={() => handleCancelVerification(verification.id)}
                                    title={t("cancel")}
                                  >
                                    <X size={16} />
                                    <span className="d-none d-md-inline">{t("cancel")}</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Message si pas de données */}
                {verificationHistory.length === 0 && (
                  <div className="text-center py-5">
                    <FileBarChart size={48} className="text-muted mb-3" />
                    <h5 className=""style={{ color: "var(--primary-color) !important"  }}>{t("no_verifications")}</h5>
                    <p className=""style={{ color: "var(--primary-color) !important"  }}>{t("no_verifications_desc")}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {renderStepIndicator()}

          {/* Les étapes de vérification restent inchangées */}
          {/* Step 1: Document Selection */}
          {verificationStep === "select" && (
            <div className="row g-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <h5 className="card-title mb-4"style={{ color: "var(--primary-color) !important"  }}>{t("select_verification_document")}</h5>
                    <div className="row g-3">
                      {documentTypes.map((doc) => {
                        const Icon = doc.icon;
                        return (
                          <div key={doc.id} className="col-md-4">
                            <div 
                              className={`card border h-100 cursor-pointer ${
                                selectedDocument === doc.id ? "border-primary shadow" : "border-light"
                              }`}
                              style={{
                                transition: "all 0.2s",
                                cursor: "pointer"
                              }}
                              onClick={() => handleDocumentSelect(doc.id)}
                              onMouseEnter={(e) => {
                                if (selectedDocument !== doc.id) {
                                  e.currentTarget.style.borderColor = "var(--primary-color3)";
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedDocument !== doc.id) {
                                  e.currentTarget.style.borderColor = "";
                                  e.currentTarget.style.transform = "";
                                }
                              }}
                            >
                              <div className="card-body text-center p-4">
                                <div 
                                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                                  style={{
                                    width: "60px",
                                    height: "60px",
                                    backgroundColor: selectedDocument === doc.id 
                                      ? "var(--primary-color5)" 
                                      : "var(--primary-color8)",
                                    color: "var(--primary-color3)"
                                  }}
                                >
                                  <Icon size={24} />
                                </div>
                                <h6 className="card-title mb-2"style={{ color: "var(--primary-color) !important"  }}>{doc.label}</h6>
                                <p className="card-text small"style={{ color: "var(--primary-color) !important"  }}>{doc.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Document Upload */}
          {verificationStep === "upload" && selectedDocument && (
            <div className="row g-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <h5 className="card-title mb-4"style={{ color: "var(--primary-color) !important"  }}>{t("upload_document_photos")}</h5>
                    
                    {/* Requirements */}
                    <div className="alert alert-info mb-4">
                      <div className="d-flex">
                        <AlertCircle size={20} className="me-2 flex-shrink-0 mt-1" />
                        <div>
                          <h6 className="alert-heading mb-2">{t("document_requirements")}</h6>
                          <ul className="mb-0 ps-3">
                            {getDocumentRequirements().map((req, index) => (
                              <li key={index}>{req}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="row g-4">
                      {/* Front Side */}
                      <div className="col-md-6">
                        <div className="text-center">
                          <h6 className="mb-3"style={{ color: "var(--primary-color) !important"  }}>{t("front_side")}</h6>
                          <div 
                            className="border rounded d-flex align-items-center justify-content-center mx-auto cursor-pointer"
                            style={{
                              width: "200px",
                              height: "150px",
                              backgroundColor: frontImage ? "transparent" : "var(--primary-color8)",
                              backgroundImage: frontImage ? `url(${frontImage})` : "none",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              cursor: "pointer"
                            }}
                            onClick={() => document.getElementById('front-upload').click()}
                          >
                            {!frontImage && (
                              <div className="text-center">
                                <Upload size={32} className="mb-2"style={{ color: "var(--primary-color) !important"  }} />
                                <div className=" small"style={{ color: "var(--primary-color) !important"  }}>{t("click_to_upload")}</div>
                              </div>
                            )}
                          </div>
                          <input
                            id="front-upload"
                            type="file"
                            accept="image/*"
                            className="d-none"style={{ color: "var(--primary-color) !important"  }}
                            onChange={(e) => handleImageUpload("front", e.target.files[0])}
                          />
                        </div>
                      </div>

                      {/* Back Side - Only for documents that require it */}
                      {documentTypes.find(d => d.id === selectedDocument)?.requiresBack && (
                        <div className="col-md-6">
                          <div className="text-center">
                            <h6 className="mb-3"style={{ color: "var(--primary-color) !important"  }}>{t("back_side")}</h6>
                            <div 
                              className="border rounded d-flex align-items-center justify-content-center mx-auto cursor-pointer"
                              style={{
                                width: "200px",
                                height: "150px",
                                backgroundColor: backImage ? "transparent" : "var(--primary-color8)",
                                backgroundImage: backImage ? `url(${backImage})` : "none",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                cursor: "pointer"
                              }}
                              onClick={() => document.getElementById('back-upload').click()}
                            >
                              {!backImage && (
                                <div className="text-center">
                                  <Upload size={32} className="mb-2"style={{ color: "var(--primary-color) !important"  }} />
                                  <div className=" small"style={{ color: "var(--primary-color) !important"  }}>{t("click_to_upload")}</div>
                                </div>
                              )}
                            </div>
                            <input
                              id="back-upload"
                              type="file"
                              accept="image/*"
                              className="d-none"style={{ color: "var(--primary-color) !important"  }}
                              onChange={(e) => handleImageUpload("back", e.target.files[0])}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => setVerificationStep("select")}
                      >
                        {t("back")}
                      </button>
                      <button 
                        className="btn btn-primary"
                        disabled={!frontImage || (documentTypes.find(d => d.id === selectedDocument)?.requiresBack && !backImage)}
                        onClick={() => setVerificationStep("selfie")}
                      >
                        {t("continue")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Selfie Verification */}
          {verificationStep === "selfie" && (
            <div className="row g-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <h5 className="card-title mb-4"style={{ color: "var(--primary-color) !important"  }}>{t("take_selfie_verification")}</h5>
                    
                    <div className="alert alert-warning mb-4">
                      <div className="d-flex">
                        <AlertCircle size={20} className="me-2 flex-shrink-0 mt-1" />
                        <div>
                          <h6 className="alert-heading mb-2">{t("selfie_requirements")}</h6>
                          <ul className="mb-0 ps-3">
                            <li>{t("selfie_requirement_face")}</li>
                            <li>{t("selfie_requirement_light")}</li>
                            <li>{t("selfie_requirement_no_glasses")}</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <div 
                        className="border rounded d-flex align-items-center justify-content-center mx-auto cursor-pointer mb-3"
                        style={{
                          width: "300px",
                          height: "300px",
                          backgroundColor: selfieImage ? "transparent" : "var(--primary-color8)",
                          backgroundImage: selfieImage ? `url(${selfieImage})` : "none",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          cursor: "pointer"
                        }}
                        onClick={() => document.getElementById('selfie-upload').click()}
                      >
                        {!selfieImage && (
                          <div className="text-center">
                            <Camera size={48} className="mb-3" style={{ color: "var(--primary-color) !important"  }}/>
                            <div className=""style={{ color: "var(--primary-color) !important"  }}>{t("click_to_take_selfie")}</div>
                          </div>
                        )}
                      </div>
                      <input
                        id="selfie-upload"
                        type="file"
                        accept="image/*"
                        capture="user"
                        className="d-none"
                        onChange={(e) => handleImageUpload("selfie", e.target.files[0])}
                      />
                      
                      {!selfieImage && (
                        <button 
                          className="btn btn-outline-primary mt-3"
                          onClick={() => document.getElementById('selfie-upload').click()}
                        >
                          <Camera size={20} className="me-2" />
                          {t("open_camera")}
                        </button>
                      )}
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => setVerificationStep("upload")}
                      >
                        {t("back")}
                      </button>
                      <button 
                        className="btn btn-primary"
                        disabled={!selfieImage}
                        onClick={() => setVerificationStep("review")}
                      >
                        {t("continue")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review and Submit */}
          {verificationStep === "review" && (
            <div className="row g-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <h5 className="card-title mb-4">{t("review_and_submit")}</h5>
                    
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <h6>{t("document_photos")}</h6>
                        <div className="d-flex gap-3">
                          <div 
                            className="border rounded"
                            style={{
                              width: "120px",
                              height: "90px",
                              backgroundImage: `url(${frontImage})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center"
                            }}
                          />
                          {backImage && (
                            <div 
                              className="border rounded"
                              style={{
                                width: "120px",
                                height: "90px",
                                backgroundImage: `url(${backImage})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center"
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <h6>{t("selfie_photo")}</h6>
                        <div 
                          className="border rounded"
                          style={{
                            width: "120px",
                            height: "120px",
                            backgroundImage: `url(${selfieImage})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            borderRadius: "50%"
                          }}
                        />
                      </div>
                    </div>

                    <div className="alert alert-info">
                      <div className="d-flex">
                        <ShieldCheck size={20} className="me-2 flex-shrink-0 mt-1" />
                        <div>
                          <h6 className="alert-heading mb-2">{t("verification_notice")}</h6>
                          <p className="mb-0">{t("verification_notice_desc")}</p>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => setVerificationStep("selfie")}
                      >
                        {t("back")}
                      </button>
                      <button 
                        className="btn btn-primary"
                        disabled={isVerifying}
                        onClick={handleVerificationSubmit}
                      >
                        {isVerifying ? (
                          <>
                            <Clock size={20} className="me-2" />
                            {t("submitting")}
                          </>
                        ) : (
                          <>
                            <ShieldCheck size={20} className="me-2" />
                            {t("submit_verification")}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success Step */}
          {verificationStep === "success" && (
            <div className="row justify-content-center">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm text-center">
                  <div className="card-body p-5">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4 bg-success bg-opacity-10"
                      style={{
                        width: "80px",
                        height: "80px",
                        color: "var(--success)"
                      }}
                    >
                      <CheckCircle2 size={40} />
                    </div>
                    <h3 className="card-title mb-3 text-success">{t("verification_submitted")}</h3>
                    <p className="card-text text-muted mb-4">
                      {t("verification_submitted_desc")}
                    </p>
                    <div className="d-flex justify-content-center gap-3">
                      <button 
                        className="btn btn-primary"
                        onClick={resetForm}
                      >
                        {t("verify_another_document")}
                      </button>
                      <button 
                        className="btn btn-outline-primary"
                        onClick={fetchVerificationHistory}
                      >
                        <RefreshCw size={20} className="me-2" />
                        {t("view_status")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de détails */}
      {selectedVerification && (
        <div className={`modal fade ${detailModalVisible ? 'show d-block' : ''}`} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title fw-bold">
                  <Eye size={20} className="me-2" />
                  {t("verification_details")}
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white"
                  onClick={() => setDetailModalVisible(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="fw-bold text-muted mb-3">{t("verification_information")}</h6>
                    <div className="mb-3">
                      <label className="form-label text-muted small">{t("document_type")}</label>
                      <div className="d-flex align-items-center gap-2">
                        {getDocumentIcon(selectedVerification.document_type)}
                        <span className="fw-medium text-capitalize">
                          {selectedVerification.document_type?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label text-muted small">{t("status")}</label>
                      <div>
                        {(() => {
                          const statusConfig = getStatusConfig(selectedVerification.status);
                          return (
                            <span className={`badge border rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 ${statusConfig.color}`}>
                              {statusConfig.icon}
                              {statusConfig.text}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label text-muted small">{t("submission_date")}</label>
                      <div className="fw-medium">{formatDate(selectedVerification.created_at)}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold text-muted mb-3">{t("verification_results")}</h6>
                    <div className="mb-3">
                      <label className="form-label text-muted small">{t("confidence_score")}</label>
                      <div className={`fw-bold fs-4 ${getScoreColor(selectedVerification.face_match_score)}`}>
                        {selectedVerification.face_match_score ? 
                          `${(selectedVerification.face_match_score * 100).toFixed(1)}%` : 
                          <span className="text-muted">-</span>
                        }
                      </div>
                    </div>
                    {selectedVerification.verified_at && (
                      <div className="mb-3">
                        <label className="form-label text-muted small">{t("completed_at")}</label>
                        <div className="fw-medium">{formatDate(selectedVerification.verified_at)}</div>
                      </div>
                    )}
                    {selectedVerification.rejection_reason && (
                      <div className="mb-3">
                        <label className="form-label text-muted small">{t("rejection_reason")}</label>
                        <div className="alert alert-light border">
                          {selectedVerification.rejection_reason}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setDetailModalVisible(false)}
                >
                  {t("close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdentityVerification;